import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Prompt, PromptTypeType } from '../../../types/backend';
import { slugifyPromptName } from '../utils/promptUtils';
import { usePromptManagement, PromptFormData } from './usePromptManagement';
import { usePromptService } from '../services/promptService';

interface UsePromptFormOptions {
  customerId: string | undefined;
  isOpen: boolean;
  onClose: () => void;
}

/** The slice of a prompt version the editor renders. */
export interface PromptVersionContent {
  content: string;
  availableVersions: string[];
  variables: string[];
}

// One key per (prompt, version): selecting a prompt seeds DRAFT, the version picker
// reads published versions, and both share this cache.
export const promptVersionKey = (
  promptId: string,
  version: string,
  owner: string,
  customerId: string | undefined,
) => ['prompt-studio', 'version', promptId, version, owner, customerId ?? null] as const;

export function usePromptForm({ customerId, isOpen, onClose }: UsePromptFormOptions) {
  const [showGlobal, setShowGlobal] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [justPublished, setJustPublished] = useState(false);
  const [viewingVersion, setViewingVersion] = useState<string | null>(null);
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const queryClient = useQueryClient();

  const promptService = usePromptService();
  const { promptService: managementPromptService, entityManagement } = usePromptManagement(customerId, isOpen);

  const {
    entities: allPrompts,
    loading,
    error,
    selectedEntity: selectedPrompt,
    formData,
    saving,
    isNew: isNewPrompt,
    showDeleteConfirm,
    successMessage,
    loadEntities,
    handleSelect: handleSelectPrompt,
    handleAddNew,
    handleSave,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleChange,
    setError,
    setSuccessMessage,
    isFormValid,
    hasChanges,
    fieldErrors,
    hasAttemptedSave,
  } = entityManagement;

  const handleChangeRef = useRef(handleChange);
  handleChangeRef.current = handleChange;

  // Per-session cache of fetched Prompt Manager content, keyed `${promptId}::${version}`.
  // Selecting a prompt costs a slow Bedrock GetPrompt, so a version's content is held in
  // the query cache until explicitly busted (staleTime: Infinity) — re-selection and version
  // switching are instant. Busted on save/publish below, where DRAFT content/versions change.
  const fetchVersion = useCallback((prompt: Prompt, version: string): Promise<PromptVersionContent> =>
    queryClient.fetchQuery({
      queryKey: promptVersionKey(prompt.id, version, prompt.owner, customerId),
      queryFn: async () => {
        const data = await managementPromptService.getPromptVersion(
          prompt.id, version, prompt.owner,
          prompt.owner === 'customer' ? customerId : undefined
        );
        return {
          content: data.content || '',
          availableVersions: data.availableVersions || [],
          variables: data.variables || [],
        };
      },
      staleTime: Infinity,
    }), [queryClient, managementPromptService, customerId]);

  const fetchAndSelect = useCallback(async (prompt: Prompt) => {
    if (prompt.content) {
      handleSelectPrompt(prompt);
      return;
    }
    try {
      handleSelectPrompt({ ...prompt, ...(await fetchVersion(prompt, 'DRAFT')) });
    } catch {
      handleSelectPrompt({ ...prompt, content: '' });
    }
  }, [handleSelectPrompt, fetchVersion]);

  const handleSelectPromptWithReset = useCallback((prompt: Prompt) => {
    setJustPublished(false);
    fetchAndSelect(prompt);
  }, [fetchAndSelect]);

  // Tracks which prompt ids already have content loaded, so we fetch once per id (avoids a
  // refetch loop on genuinely-empty content). Cleared for a prompt on save/publish, where the
  // list re-select wipes content back to null and we must reload it (from cache, instantly).
  const fetchedIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const p = selectedPrompt;
    if (p && !p.content && !fetchedIdsRef.current.has(p.id)) {
      fetchedIdsRef.current.add(p.id);
      fetchAndSelect(p);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPrompt?.id, selectedPrompt?.content]);

  const handleAddNewWithReset = useCallback((defaultType?: PromptTypeType) => {
    handleAddNew();
    if (defaultType) handleChangeRef.current('type', defaultType);
  }, [handleAddNew]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 1000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, setSuccessMessage]);

  useEffect(() => {
    if (isNewPrompt && formData.title) {
      const slug = slugifyPromptName(formData.title, formData.type);
      if (slug) handleChangeRef.current('name', slug);
    }
  }, [isNewPrompt, formData.title, formData.type]);

  const displayedPrompts = useMemo(() => {
    const customer = allPrompts.filter(p => p.owner === 'customer' && p.customerId === customerId);
    const global = allPrompts.filter(p => p.owner === 'global');
    customer.sort((a, b) => a.title.localeCompare(b.title));
    global.sort((a, b) => a.title.localeCompare(b.title));
    return showGlobal ? [...customer, ...global] : customer;
  }, [allPrompts, showGlobal, customerId]);

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    for (const p of displayedPrompts) {
      for (const t of p.tags) tagSet.add(t);
    }
    return Array.from(tagSet).sort();
  }, [displayedPrompts]);

  const handlePublish = useCallback(async () => {
    if (!selectedPrompt) return;
    setPublishing(true);
    try {
      await managementPromptService.publishPrompt(
        selectedPrompt.id,
        selectedPrompt.owner,
        customerId
      );
      await loadEntities();
      // Publishing mints a new version — drop the cached DRAFT (stale availableVersions) and
      // let the post-reload re-select refetch fresh content + versions from Bedrock.
      queryClient.removeQueries({
        queryKey: promptVersionKey(selectedPrompt.id, 'DRAFT', selectedPrompt.owner, customerId),
      });
      fetchedIdsRef.current.delete(selectedPrompt.id);
      setJustPublished(true);
      setSuccessMessage('Published successfully');
    } catch (err) {
      setError(`Failed to publish: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setPublishing(false);
    }
  }, [selectedPrompt, managementPromptService, customerId, loadEntities, setSuccessMessage, setError, queryClient]);

  const handleSaveWithCacheBust = useCallback(() => {
    const entity = selectedPrompt;
    if (entity) {
      // Seed the cache with the content we're about to save BEFORE handleSave's internal list
      // re-select fires — that re-select carries content:null and would otherwise blank the
      // editor. Clearing the fetched-id lets the auto-loader restore it from cache instantly.
      queryClient.setQueryData<PromptVersionContent>(
        promptVersionKey(entity.id, 'DRAFT', entity.owner, customerId),
        {
          content: formData.content,
          availableVersions: entity.availableVersions ?? [],
          variables: entity.variables ?? [],
        },
      );
      fetchedIdsRef.current.delete(entity.id);
    }
    return handleSave();
  }, [handleSave, selectedPrompt, formData, queryClient, customerId]);

  useEffect(() => {
    setSettingsExpanded(isNewPrompt);
  }, [isNewPrompt, selectedPrompt]);

  useEffect(() => {
    setViewingVersion(null);
  }, [selectedPrompt]);

  // A published version's content. Immutable once minted, so it stays cached —
  // switching back and forth between versions costs nothing.
  const versionQuery = useQuery({
    queryKey: selectedPrompt && viewingVersion
      ? promptVersionKey(selectedPrompt.id, viewingVersion, selectedPrompt.owner, customerId)
      : ['prompt-studio', 'version', 'none'],
    queryFn: async () => {
      if (!selectedPrompt || !viewingVersion) {
        throw new Error('Cannot load a prompt version without a selected prompt and version');
      }
      const data = await promptService.getPromptVersion(
        selectedPrompt.id, viewingVersion, selectedPrompt.owner,
        selectedPrompt.owner === 'customer' ? customerId : undefined
      );
      return {
        content: data.content || '',
        availableVersions: data.availableVersions || [],
        variables: data.variables || [],
      };
    },
    enabled: !!selectedPrompt && !!viewingVersion,
    staleTime: Infinity,
  });

  const viewingContent = versionQuery.data?.content ?? '';
  const versionLoading = versionQuery.isLoading;

  // A failed version read drops the preview back to DRAFT and reports it — the editor
  // must never present empty content as if it were the version.
  const failedVersion = versionQuery.isError ? viewingVersion : null;
  useEffect(() => {
    if (failedVersion) {
      setError(`Failed to load version ${failedVersion}`);
      setViewingVersion(null);
    }
  }, [failedVersion, setError]);

  const handleVersionChange = useCallback((version: string) => {
    if (!selectedPrompt) return;
    setViewingVersion(version === 'DRAFT' ? null : version);
  }, [selectedPrompt]);

  const handleContentChange = useCallback(<K extends keyof PromptFormData>(field: K, value: PromptFormData[K]) => {
    if (justPublished) setJustPublished(false);
    handleChange(field, value);
  }, [justPublished, handleChange]);

  return {
    // Entity data
    loading,
    error,
    selectedPrompt,
    formData,
    saving,
    isNewPrompt,
    showDeleteConfirm,
    successMessage,
    displayedPrompts,
    allTags,
    customerId,

    // UI state
    showGlobal,
    setShowGlobal,
    publishing,
    hasAttemptedSave,
    justPublished,
    viewingVersion,
    viewingContent,
    versionLoading,
    settingsExpanded,
    setSettingsExpanded,
    isViewingHistory: viewingVersion !== null,

    // Validity
    isFormValid,
    hasChanges,
    fieldErrors,

    // Handlers
    handleSelect: handleSelectPromptWithReset,
    handleAddNew: handleAddNewWithReset,
    handleSave: handleSaveWithCacheBust,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    handleChange: handleContentChange,
    handleClose,
    handlePublish,
    handleVersionChange,
    setError,
    setSuccessMessage,
  };
}
